import type { PageOrientation } from '@record-fill';
import { A4_HEIGHT, A4_WIDTH, CANVAS_LAYOUT, PORTRAIT_DISPLAY_SCALE } from './canvas-constants';
import { DEFAULT_FIRST_ELEMENT_AFTER_HEADER_MARGIN_TOP } from './element-defaults';

/** Width of the portrait page the builder draws, where the author sees where each page ends. */
export const PORTRAIT_SHEET_WIDTH = Math.round(A4_WIDTH * PORTRAIT_DISPLAY_SCALE);

/** Width of the landscape page the builder draws: the portrait page turned, at the same scale. */
export const LANDSCAPE_SHEET_WIDTH = Math.round(A4_HEIGHT * PORTRAIT_DISPLAY_SCALE);

export function sheetWidthFor(orientation: PageOrientation): number {
  return orientation === 'landscape' ? LANDSCAPE_SHEET_WIDTH : PORTRAIT_SHEET_WIDTH;
}

export function computePageHeight(
  displayWidth: number,
  orientation: PageOrientation = 'portrait'
): number {
  const [pageWidth, pageHeight] =
    orientation === 'landscape' ? [A4_HEIGHT, A4_WIDTH] : [A4_WIDTH, A4_HEIGHT];
  return Math.round((displayWidth / pageWidth) * pageHeight);
}

export function computePageCount(sheetHeight: number, pageHeight: number): number {
  if (pageHeight <= 0) return 1;
  return Math.max(1, Math.ceil(sheetHeight / pageHeight));
}

/** The room the builder canvas leaves around the content of each page, in px of the sheet. */
export interface SheetGeometry {
  pageHeight: number;
  pagePadding: number;
  bottomMargin: number;
  topMarginWithoutHeader: number;
  movedTableGap: number;
  continuedTableGap: number;
}

export function computeSheetGeometry(
  displayWidth: number,
  orientation: PageOrientation = 'portrait'
): SheetGeometry {
  return {
    pageHeight: computePageHeight(displayWidth, orientation),
    pagePadding: CANVAS_LAYOUT.CANVAS_PADDING,
    bottomMargin: CANVAS_LAYOUT.BOTTOM_MARGIN,
    topMarginWithoutHeader: CANVAS_LAYOUT.TOP_MARGIN,
    movedTableGap: CANVAS_LAYOUT.HEADER_TO_CONTENT_GAP,
    continuedTableGap: DEFAULT_FIRST_ELEMENT_AFTER_HEADER_MARGIN_TOP,
  };
}
